import {
  SyncStatusResponseSchema,
  FormDefinitionSchema,
  FormsResponseSchema,
  ServiceTypesResponseSchema,
  ServiceTypeSchema,
  ProgramQuestionSchema,
  ProgramQuestionsResponseSchema,
  ProgramsResponseSchema,
  ProgramConfigSchema,
  ProgramConfigsResponseSchema,
  RolloutLocationsResponseSchema,
  LocationRolloutResponseSchema,
  KioskDevicesResponseSchema,
  FormModulesResponseSchema,
  FormModuleEntityLinksResponseSchema,
  FormModuleNameAvailabilitySchema,
  FormModuleSchema,
  FormModuleDetailSchema,
  FormModuleVersionSchema,
  PublishedFormModulesResponseSchema,
  FormModuleTemplatesResponseSchema,
  type FormModule,
  type FormModuleTemplatesResponse,
  type PublishFormModule,
  type DiscardFormModule,
  type FormModuleDetail,
  type FormModuleVersion,
  type TemplateModule,
  type UpdateFormModule,
  type CreateFormModule,
  type FormModuleEntityLink,
  type RolloutLocation,
  type ProgramChannels,
  type SaveProgramChannels,
  type KioskDeviceSummary,
  type SyncStatusResponse,
  type FormDefinition,
  type FormSummary,
  type UpdateFormDefinition,
  type ServiceType,
  type ProgramQuestion,
  type ProgramQuestionRow,
  type UpsertProgramQuestion,
  type Bracket,
  type ProgramSummary,
  type ProgramConfig,
  type UpdateProgramConfig,
  SessionTemplatesResponseSchema,
  SessionTemplateSchema,
  SessionTemplateDetailSchema,
  SessionTemplateNameAvailabilitySchema,
  type SessionTemplate,
  type SessionTemplateDetail,
  type CreateSessionTemplate,
  type UpdateSessionTemplate,
} from "shared"
import { authedFetch } from "@rawa/api-client/authedFetch"
import { parseApiError } from "@rawa/api-client/ApiResponseError"

export async function fetchSyncStatus(): Promise<SyncStatusResponse> {
  const res = await authedFetch("/api/admin/sync-status")
  if (!res.ok) throw await parseApiError(res)
  return SyncStatusResponseSchema.parse(await res.json())
}

export async function fetchServiceTypes(
  signal?: AbortSignal,
): Promise<ServiceType[]> {
  const res = await authedFetch("/api/admin/service-types", { signal })
  if (!res.ok) throw await parseApiError(res)
  return ServiceTypesResponseSchema.parse(await res.json()).serviceTypes
}

export async function classifyServiceType(
  id: string,
  isContact: boolean,
): Promise<ServiceType> {
  const res = await authedFetch(
    `/api/admin/service-types/${encodeURIComponent(id)}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ isContact }),
    },
  )
  if (!res.ok) throw await parseApiError(res)
  return ServiceTypeSchema.parse(await res.json())
}

export async function fetchForms(): Promise<FormSummary[]> {
  const res = await authedFetch("/api/admin/forms")
  if (!res.ok) throw await parseApiError(res)
  return FormsResponseSchema.parse(await res.json()).forms
}

export async function fetchFormDefinition(
  formId: string,
): Promise<FormDefinition> {
  const res = await authedFetch(
    `/api/admin/forms/${encodeURIComponent(formId)}`,
  )
  if (!res.ok) throw await parseApiError(res)
  return FormDefinitionSchema.parse(await res.json())
}

export async function updateFormDefinition(
  formId: string,
  body: UpdateFormDefinition,
): Promise<FormDefinition> {
  const res = await authedFetch(
    `/api/admin/forms/${encodeURIComponent(formId)}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  )
  if (!res.ok) throw await parseApiError(res)
  return FormDefinitionSchema.parse(await res.json())
}

export async function fetchProgramQuestions(): Promise<ProgramQuestionRow[]> {
  const res = await authedFetch("/api/admin/program-questions")
  if (!res.ok) throw await parseApiError(res)
  return ProgramQuestionsResponseSchema.parse(await res.json()).programQuestions
}

export async function updateProgramQuestion(
  id: string,
  body: UpsertProgramQuestion,
): Promise<ProgramQuestion> {
  const res = await authedFetch(
    `/api/admin/program-questions/${encodeURIComponent(id)}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  )
  if (!res.ok) throw await parseApiError(res)
  return ProgramQuestionSchema.parse(await res.json())
}

export async function fetchAdminPrograms(
  signal?: AbortSignal,
): Promise<ProgramSummary[]> {
  const res = await authedFetch("/api/admin/programs", { signal })
  if (!res.ok) throw await parseApiError(res)
  return ProgramsResponseSchema.parse(await res.json()).programs
}

export async function deleteProgramQuestion(
  programId: string,
  id: string,
  bracket: Bracket,
): Promise<void> {
  const res = await authedFetch(
    `/api/admin/program-questions/${encodeURIComponent(programId)}/${encodeURIComponent(id)}/${encodeURIComponent(bracket)}`,
    { method: "DELETE" },
  )
  if (!res.ok) throw await parseApiError(res)
}

export async function fetchProgramConfig(): Promise<ProgramConfig[]> {
  const res = await authedFetch("/api/admin/program-config")
  if (!res.ok) throw await parseApiError(res)
  return ProgramConfigsResponseSchema.parse(await res.json()).programConfigs
}

export async function updateProgramConfig(
  id: string,
  body: UpdateProgramConfig,
): Promise<ProgramConfig> {
  const res = await authedFetch(
    `/api/admin/program-config/${encodeURIComponent(id)}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  )
  if (!res.ok) throw await parseApiError(res)
  return ProgramConfigSchema.parse(await res.json())
}

export async function fetchKioskDevices(): Promise<KioskDeviceSummary[]> {
  const res = await authedFetch("/api/admin/kiosk-devices")
  if (!res.ok) throw await parseApiError(res)
  return KioskDevicesResponseSchema.parse(await res.json()).kioskDevices
}

export async function revokeKioskDevice(id: string): Promise<void> {
  const res = await authedFetch(
    `/api/admin/kiosk-devices/${encodeURIComponent(id)}/revoke`,
    { method: "POST" },
  )
  if (!res.ok) throw await parseApiError(res)
}

export async function deleteKioskDevice(id: string): Promise<void> {
  const res = await authedFetch(
    `/api/admin/kiosk-devices/${encodeURIComponent(id)}`,
    { method: "DELETE" },
  )
  if (!res.ok) throw await parseApiError(res)
}

export async function fetchRolloutLocations(): Promise<RolloutLocation[]> {
  const res = await authedFetch("/api/admin/rollout")
  if (!res.ok) throw await parseApiError(res)
  return RolloutLocationsResponseSchema.parse(await res.json()).locations
}

export async function fetchLocationRollout(
  locationId: string,
): Promise<ProgramChannels[]> {
  const res = await authedFetch(`/api/admin/rollout/${locationId}`)
  if (!res.ok) throw await parseApiError(res)
  return LocationRolloutResponseSchema.parse(await res.json()).programs
}

// Replaces the location's whole rollout, not just what changed — see
// SaveLocationRolloutSchema.
export async function saveLocationRollout(
  locationId: string,
  programs: SaveProgramChannels[],
): Promise<void> {
  const res = await authedFetch(`/api/admin/rollout/${locationId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ programs }),
  })
  if (!res.ok) throw await parseApiError(res)
}

/** The modules on the builder's list, narrowed by `search` when one is given. */
export async function fetchFormModules(
  search?: string,
  signal?: AbortSignal,
): Promise<FormModule[]> {
  const term = search?.trim() ?? ""
  const query = term === "" ? "" : `?search=${encodeURIComponent(term)}`
  const res = await authedFetch(`/api/admin/form-modules${query}`, { signal })
  if (!res.ok) throw await parseApiError(res)
  return FormModulesResponseSchema.parse(await res.json()).formModules
}

/**
 * The modules a session template can add, as their latest versions publish
 * them — never a working copy (ADR-040).
 */
export async function fetchPublishedFormModules(
  signal?: AbortSignal,
): Promise<TemplateModule[]> {
  const res = await authedFetch("/api/admin/form-modules/published", { signal })
  if (!res.ok) throw await parseApiError(res)
  return PublishedFormModulesResponseSchema.parse(await res.json()).formModules
}

/** The session templates on the list, narrowed by `search` when one is given. */
export async function fetchSessionTemplates(
  search?: string,
  signal?: AbortSignal,
): Promise<SessionTemplate[]> {
  const term = search?.trim() ?? ""
  const query = term === "" ? "" : `?search=${encodeURIComponent(term)}`
  const res = await authedFetch(`/api/admin/session-templates${query}`, {
    signal,
  })
  if (!res.ok) throw await parseApiError(res)
  return SessionTemplatesResponseSchema.parse(await res.json()).sessionTemplates
}

export async function fetchSessionTemplate(
  id: string,
  signal?: AbortSignal,
): Promise<SessionTemplateDetail> {
  const res = await authedFetch(`/api/admin/session-templates/${id}`, {
    signal,
  })
  if (!res.ok) throw await parseApiError(res)
  return SessionTemplateDetailSchema.parse(await res.json())
}

export async function updateSessionTemplate(
  id: string,
  template: UpdateSessionTemplate,
): Promise<SessionTemplateDetail> {
  const res = await authedFetch(
    `/api/admin/session-templates/${encodeURIComponent(id)}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(template),
    },
  )
  if (!res.ok) throw await parseApiError(res)
  return SessionTemplateDetailSchema.parse(await res.json())
}

export async function isSessionTemplateNameAvailable(
  name: string,
  signal?: AbortSignal,
): Promise<boolean> {
  const res = await authedFetch(
    `/api/admin/session-templates/name-available?name=${encodeURIComponent(name)}`,
    { signal },
  )
  if (!res.ok) throw await parseApiError(res)
  return SessionTemplateNameAvailabilitySchema.parse(await res.json()).available
}

export async function createSessionTemplate(
  template: CreateSessionTemplate,
): Promise<SessionTemplate> {
  const res = await authedFetch("/api/admin/session-templates", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(template),
  })
  if (!res.ok) throw await parseApiError(res)
  return SessionTemplateSchema.parse(await res.json())
}

export async function archiveSessionTemplate(id: string): Promise<void> {
  const res = await authedFetch(
    `/api/admin/session-templates/${encodeURIComponent(id)}/archive`,
    { method: "POST" },
  )
  if (!res.ok) throw await parseApiError(res)
}

export async function fetchEntityLinks(
  signal?: AbortSignal,
): Promise<FormModuleEntityLink[]> {
  const res = await authedFetch("/api/admin/form-modules/entity-links", {
    signal,
  })
  if (!res.ok) throw await parseApiError(res)
  return FormModuleEntityLinksResponseSchema.parse(await res.json()).entityLinks
}

/**
 * Whether no other module holds the name, as its working name or the name it
 * publishes (ADR-040). Advisory — create, save and publish are what refuse a
 * duplicate, so a false here is a warning and not a guarantee. `exceptId` is
 * the module asking: its own names are free to it.
 */
export async function isFormModuleNameAvailable(
  name: string,
  signal?: AbortSignal,
  exceptId?: string,
): Promise<boolean> {
  const query = new URLSearchParams({ name })
  if (exceptId) query.set("exceptId", exceptId)
  const res = await authedFetch(
    `/api/admin/form-modules/name-available?${query.toString()}`,
    { signal },
  )
  if (!res.ok) throw await parseApiError(res)
  return FormModuleNameAvailabilitySchema.parse(await res.json()).available
}

export async function createFormModule(
  body: CreateFormModule,
): Promise<FormModule> {
  const res = await authedFetch("/api/admin/form-modules", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  })
  if (!res.ok) throw await parseApiError(res)
  return FormModuleSchema.parse(await res.json())
}

/** Takes a module off the builder's list. Its name stays taken. */
export async function archiveFormModule(id: string): Promise<void> {
  const res = await authedFetch(
    `/api/admin/form-modules/${encodeURIComponent(id)}/archive`,
    { method: "POST" },
  )
  if (!res.ok) throw await parseApiError(res)
}

export async function fetchFormModule(
  id: string,
  signal?: AbortSignal,
): Promise<FormModuleDetail> {
  const res = await authedFetch(
    `/api/admin/form-modules/${encodeURIComponent(id)}`,
    { signal },
  )
  if (!res.ok) throw await parseApiError(res)
  return FormModuleDetailSchema.parse(await res.json())
}

/** One version of a module, with the wording and fields it was published with. */
export async function fetchFormModuleVersion(
  id: string,
  version: number,
  signal?: AbortSignal,
): Promise<FormModuleVersion> {
  const res = await authedFetch(
    `/api/admin/form-modules/${encodeURIComponent(id)}/versions/${version}`,
    { signal },
  )
  if (!res.ok) throw await parseApiError(res)
  return FormModuleVersionSchema.parse(await res.json())
}

export async function updateFormModule(
  id: string,
  formModule: UpdateFormModule,
): Promise<FormModuleDetail> {
  const res = await authedFetch(
    `/api/admin/form-modules/${encodeURIComponent(id)}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(formModule),
    },
  )
  if (!res.ok) throw await parseApiError(res)
  return FormModuleDetailSchema.parse(await res.json())
}

/**
 * Publishes the module's draft at the revision the canvas holds: its next
 * version, and a new version of every template placing it (ADR-040).
 */
export async function publishFormModule(
  id: string,
  publish: PublishFormModule,
): Promise<FormModuleDetail> {
  const res = await authedFetch(
    `/api/admin/form-modules/${encodeURIComponent(id)}/publish`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(publish),
    },
  )
  if (!res.ok) throw await parseApiError(res)
  return FormModuleDetailSchema.parse(await res.json())
}

/**
 * Throws away the module's saved changes at the revision the canvas holds,
 * putting its working copy back to the latest version (ADR-040).
 */
export async function discardFormModule(
  id: string,
  discard: DiscardFormModule,
): Promise<FormModuleDetail> {
  const res = await authedFetch(
    `/api/admin/form-modules/${encodeURIComponent(id)}/discard`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(discard),
    },
  )
  if (!res.ok) throw await parseApiError(res)
  return FormModuleDetailSchema.parse(await res.json())
}

/** Deletes a module never published, which frees its name (ADR-040). */
export async function deleteFormModule(id: string): Promise<void> {
  const res = await authedFetch(
    `/api/admin/form-modules/${encodeURIComponent(id)}`,
    { method: "DELETE" },
  )
  if (!res.ok) throw await parseApiError(res)
}

/** The templates a publish of this module would give a new version. */
export async function fetchFormModuleTemplates(
  id: string,
  signal?: AbortSignal,
): Promise<FormModuleTemplatesResponse> {
  const res = await authedFetch(
    `/api/admin/form-modules/${encodeURIComponent(id)}/templates`,
    { signal },
  )
  if (!res.ok) throw await parseApiError(res)
  return FormModuleTemplatesResponseSchema.parse(await res.json())
}
